import React, { useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import { FiExternalLink } from "react-icons/fi";
import { RendererRuntimeProvider, type All8PageContent } from "@bcc/core-renderers";
import PagePreviewContent from "./PagePreviewContent";
import { LoadingSpinner } from "../LoadingSpinner";
import PageConnectionsButton from "../Connections/PageConnectionsButton";

export const PREVIEW_MAX_WIDTH = 1280;
// Widths aligned with the public site's stock Tailwind breakpoints so the
// preview reproduces the SAME layout switches as the deployed site:
//   mobile 390 (< sm 640) · tablet 768 (= md) · laptop 1024 (= lg) ·
//   desktop 1280 (= xl / max-w-7xl container) · full = fluid.
export const PREVIEW_VIEWPORTS = {
	mobile: 390,
	tablet: 768,
	laptop: 1024,
	desktop: 1280,
	full: null as number | null
};
export type PreviewViewportKey = keyof typeof PREVIEW_VIEWPORTS;

/** French labels for the viewport switcher. */
export const PREVIEW_VIEWPORT_LABELS: Record<PreviewViewportKey, string> = {
	mobile: "Mobile · 390",
	tablet: "Tablette · 768",
	laptop: "Portable · 1024",
	desktop: "Bureau · 1280",
	full: "Fluide"
};

const PreviewPane: React.FC<{
	components: All8PageContent;
	width: number | null;
	containerWidth: number | null;
	runtimeConfig: any;
	/** Chosen viewport : drives the decorative device frame. */
	viewport: PreviewViewportKey;
	/** Index of the card currently open in the components panel; its block gets a subtle outline. */
	activeComponentIndex?: number | null;
	/** Called when the user clicks a top-level block inside the iframe (component index). */
	onSelectComponent?: (index: number) => void;
}> = ({ components, width, containerWidth, runtimeConfig, viewport, activeComponentIndex, onSelectComponent }) => {
	const iframeRef = useRef<HTMLIFrameElement | null>(null);
	const rootRef = useRef<ReturnType<typeof createRoot> | null>(null);
	// Refs so the delegated click listener (attached once per iframe render) always sees the
	// latest callback/selection without forcing a full iframe rebuild on every prop change.
	const onSelectRef = useRef(onSelectComponent);
	onSelectRef.current = onSelectComponent;
	const activeIndexRef = useRef<number | null>(activeComponentIndex ?? null);
	activeIndexRef.current = activeComponentIndex ?? null;
	// Origin tracking: selections that come FROM the iframe must not scroll the iframe (the user
	// is already looking at the block) : only panel-side selection changes do.
	const selectionFromPreviewRef = useRef(false);
	const lastHighlightedIndexRef = useRef<number | null>(null);

	useEffect(() => {
		const iframe = iframeRef.current;
		if (!iframe) return;
		const doc = iframe.contentDocument;
		if (!doc) return;

		doc.open();
		doc.write("<!doctype html><html><head></head><body><div id='preview-root'></div></body></html>");
		doc.close();

		const head = doc.head;
		document.querySelectorAll("link[rel='stylesheet'], style").forEach((node) => {
			head.appendChild(node.cloneNode(true));
		});

		const mountNode = doc.getElementById("preview-root");
		if (!mountNode) return;

		const root = createRoot(mountNode);
		rootRef.current = root;
		root.render(
			<RendererRuntimeProvider value={runtimeConfig}>
				<div className="page-editor-preview-inner">
					{/* Un bloc qui leve ne doit pas blanchir tout l'apercu : voir PagePreviewContent. */}
					<PagePreviewContent components={components} />
				</div>
			</RendererRuntimeProvider>
		);

		// One delegated listener on the iframe document (capture phase) links blocks back to their
		// card. It never mutates content : selection is pure UI state : so it also works in
		// read-only mode, and it leaves the existing disableLinks behavior untouched.
		const handlePreviewClick = (event: Event) => {
			const target = event.target as Element | null;
			// Sanitised paragraph HTML may carry raw <a href> anchors (not RuntimeLink
			// components, so disableLinks cannot catch them) : block their navigation
			// so a preview click never leaves the iframe.
			if (target?.closest?.("a[href]")) {
				event.preventDefault();
			}
			if (!onSelectRef.current) return;
			const wrapper = target?.closest?.("[data-editor-index]");
			if (!wrapper) return;
			const index = Number(wrapper.getAttribute("data-editor-index"));
			if (!Number.isInteger(index) || index < 0) return;
			if (index !== activeIndexRef.current) {
				selectionFromPreviewRef.current = true;
			}
			onSelectRef.current(index);
		};
		doc.addEventListener("click", handlePreviewClick, true);

		return () => {
			doc.removeEventListener("click", handlePreviewClick, true);
			root.unmount();
		};
	}, [components, runtimeConfig]);

	// Tag the renderer's top-level wrappers with their component index, then reflect the active
	// selection. PageRenderer emits <div>{components.map(… → <div>)}</div>, so the children of its
	// root div map 1:1 onto the components array (even an unmapped type keeps its wrapper) : // tagging after the fact keeps PageRenderer untouched and its sibling-aware spacing intact.
	// createRoot renders asynchronously, hence the small retry loop over animation frames.
	useEffect(() => {
		const doc = iframeRef.current?.contentDocument;
		if (!doc) return;
		let cancelled = false;
		let raf = 0;
		let attempts = 0;
		const apply = () => {
			if (cancelled) return;
			const rendererRoot = doc.querySelector(".page-editor-preview-inner > div");
			const wrappers = rendererRoot ? Array.from(rendererRoot.children) : [];
			if (wrappers.length < components.length && attempts < 20) {
				attempts += 1;
				raf = requestAnimationFrame(apply);
				return;
			}
			const activeIndex = activeComponentIndex ?? null;
			wrappers.forEach((wrapper, index) => {
				const el = wrapper as HTMLElement;
				el.setAttribute("data-editor-index", String(index));
				const isActive = activeIndex === index;
				el.style.outline = isActive ? "2px solid rgba(0,92,169,.4)" : "";
				el.style.outlineOffset = isActive ? "2px" : "";
			});
			const activeChanged = lastHighlightedIndexRef.current !== activeIndex;
			lastHighlightedIndexRef.current = activeIndex;
			const fromPreview = selectionFromPreviewRef.current;
			if (activeChanged) {
				selectionFromPreviewRef.current = false;
			}
			// Scroll the iframe only when the selection changed from the PANEL side; iframe-originated
			// clicks skip it to avoid scroll fights.
			if (activeChanged && !fromPreview && activeIndex != null) {
				const target = wrappers[activeIndex] as HTMLElement | undefined;
				if (target && typeof target.scrollIntoView === "function") {
					target.scrollIntoView({ block: "nearest" });
				}
			}
		};
		raf = requestAnimationFrame(apply);
		return () => {
			cancelled = true;
			cancelAnimationFrame(raf);
		};
	}, [components, runtimeConfig, activeComponentIndex]);

	// Lock iframe layout to the chosen viewport width (mobile/tablet/desktop) so content renders at real size
	useEffect(() => {
		const iframe = iframeRef.current;
		if (!iframe?.contentDocument?.head) return;
		let meta = iframe.contentDocument.querySelector('meta[name="viewport"]');
		if (!meta) {
			meta = iframe.contentDocument.createElement("meta");
			meta.setAttribute("name", "viewport");
			iframe.contentDocument.head.appendChild(meta);
		}
		meta.setAttribute("content", width != null ? `width=${width}` : "width=device-width");
	}, [width]);

	const frameWidth = width ? Math.min(width, PREVIEW_MAX_WIDTH) : undefined;
	// Use a 2px buffer when scaling down so the full content fits (avoids right-edge clipping, esp. tablet)
	const fitBuffer = 2;
	// Cap scale for tablet/desktop (960px+) so the preview doesn’t feel overzoomed; keep full content visible with margin
	const maxScaleTabletDesktop = 0.9;
	// When container width is unknown, use full-width mode so we never overflow (stable on small window + reload)
	const hasValidContainer = containerWidth != null && containerWidth > 0;
	const effectiveContainerWidth = hasValidContainer ? containerWidth : null;
	const fixedViewport = frameWidth != null && effectiveContainerWidth != null;
	const rawScale =
		fixedViewport
			? Math.min(1, Math.max(0.3, (effectiveContainerWidth - fitBuffer) / frameWidth))
			: 1;
	const scale =
		frameWidth != null && frameWidth >= PREVIEW_VIEWPORTS.tablet
			? Math.min(rawScale, maxScaleTabletDesktop)
			: rawScale;

	// Fixed viewport (mobile/tablet/desktop): render at real size (frameWidth), scale to fit container.
	// Wrapper uses displayed size (frameWidth * scale) so layout fits and doesn’t overflow.
	const rawDisplayWidth = fixedViewport ? frameWidth * scale : null;
	const displayWidthPx =
		rawDisplayWidth != null && effectiveContainerWidth != null
			? Math.min(rawDisplayWidth, effectiveContainerWidth)
			: rawDisplayWidth;
	const innerHeight = fixedViewport && scale < 1 ? `${100 / scale}%` : "100%";
	// Center the preview when the panel is wider than the scaled frame (e.g. mobile on a big panel)
	const centerInPanel =
		fixedViewport && displayWidthPx != null && effectiveContainerWidth != null && displayWidthPx < effectiveContainerWidth;

	// Decorative device frame (box-shadow + radius only : never changes layout
	// width, so the scale-to-fit math above is untouched).
	const isHandheld = viewport === "mobile" || viewport === "tablet";
	const isDesktopFrame = viewport === "laptop" || viewport === "desktop";
	const frameStyle: React.CSSProperties = fixedViewport
		? isHandheld
			? { borderRadius: 22, boxShadow: "0 0 0 6px #0F2849, 0 12px 30px rgba(15,40,73,0.18)" }
			: { borderRadius: 12, boxShadow: "0 0 0 1px #cbd5e1, 0 10px 26px rgba(15,40,73,0.12)" }
		: {};

	return (
		<div
			className="page-editor-preview h-full"
			data-preview-viewport={viewport}
			style={{
				width: displayWidthPx != null ? `${displayWidthPx}px` : "100%",
				maxWidth: "100%",
				height: "100%",
				marginLeft: centerInPanel ? "auto" : 0,
				marginRight: "auto",
				overflow: "hidden",
				minWidth: 0,
				...frameStyle
			}}
		>
			<div
				style={{
					width: fixedViewport ? `${frameWidth}px` : "100%",
					height: innerHeight,
					transform: fixedViewport ? `scale(${scale})` : "none",
					transformOrigin: "top left"
				}}
			>
				{isDesktopFrame && (
					<div className="flex items-center gap-1.5 bg-slate-100 px-3 py-2" aria-hidden="true">
						<span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
						<span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
						<span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
					</div>
				)}
				{isHandheld && (
					<div className="flex items-center justify-center bg-[#0F2849] py-1.5" aria-hidden="true">
						<span className="h-1 w-10 rounded-full bg-white/40" />
					</div>
				)}
				<iframe
					ref={iframeRef}
					title="Page preview"
					style={{
						width: "100%",
						border: "none",
						height: "100%",
						background: "white",
						overflow: "auto"
					}}
				/>
			</div>
		</div>
	);
};

type PageEditorPreviewPanelProps = {
	previewViewport: PreviewViewportKey;
	onViewportChange: (viewport: PreviewViewportKey) => void;
	/** URL to open in new tab for preview site; empty hides the button */
	previewPageUrl: string;
	/** URL to open in new tab for production site; empty hides the button */
	productionPageUrl: string;
	previewContainerRef: React.RefObject<HTMLDivElement>;
	previewCss: string;
	previewComponents: All8PageContent;
	previewWidth: number | null;
	previewContainerWidth: number | null;
	runtimeConfig: any;
	dynamicPreviewLabel?: string | null;
	onOpenDynamicPreview?: () => void;
	onClearDynamicPreview?: () => void;
	/** Optional min height for the preview container (px or CSS value). */
	minHeight?: number | string;
	/** Optional fixed height for the preview container (px or CSS value). */
	contentHeight?: number | string;
	/** When set, shows a "Connexions" button that opens this page's connections graph. */
	pageId?: string | null;
	pageTitle?: string | null;
	/** Index of the card currently open in the components panel; highlights its block in the
	 *  iframe and scrolls it into view on panel-side selection changes. */
	activeComponentIndex?: number | null;
	/** Called when the user clicks a top-level block inside the preview iframe (component index).
	 *  Selection only : never mutates content, so it is also passed in read-only mode. */
	onSelectComponent?: (index: number) => void;
};

/** Live page preview panel with responsive viewport simulation in an iframe. */
const PageEditorPreviewPanel: React.FC<PageEditorPreviewPanelProps> = ({
	previewViewport,
	onViewportChange,
	previewPageUrl,
	productionPageUrl,
	previewContainerRef,
	previewCss,
	previewComponents,
	previewWidth,
	previewContainerWidth,
	runtimeConfig,
	dynamicPreviewLabel,
	onOpenDynamicPreview,
	onClearDynamicPreview,
	minHeight,
	contentHeight,
	pageId,
	pageTitle,
	activeComponentIndex,
	onSelectComponent
}) => (
	<div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4 flex flex-col h-full min-h-0">
		<div className="flex flex-wrap items-center justify-between gap-3">
			<div>
				<p className="text-xs uppercase tracking-[0.3em] text-slate-400">Aperçu en direct</p>
				<p className="text-sm text-slate-600">Les liens sont désactivés ici. Choisissez une taille d'écran pour simuler la mise en page responsive.</p>
			</div>
			<div className="flex flex-wrap items-center gap-2">
				{onOpenDynamicPreview && (
					<button
						type="button"
						onClick={onOpenDynamicPreview}
						className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-slate-300 hover:bg-slate-50"
					>
						Aperçu avec un élément de contenu
					</button>
				)}
				{dynamicPreviewLabel && (
					<span className="text-xs text-slate-500">
						Aperçu : <span className="font-semibold text-slate-700">{dynamicPreviewLabel}</span>
					</span>
				)}
				{dynamicPreviewLabel && onClearDynamicPreview && (
					<button
						type="button"
						onClick={onClearDynamicPreview}
						className="rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-500 hover:border-slate-300 hover:bg-slate-50"
					>
						Réinitialiser l'aperçu
					</button>
				)}
				{(Object.keys(PREVIEW_VIEWPORTS) as PreviewViewportKey[]).map((key) => {
					const isActive = previewViewport === key;
					return (
						<button
							key={key}
							type="button"
							onClick={() => onViewportChange(key)}
							className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
								isActive
									? "border border-[#005CA9] bg-[#005CA9] text-white shadow-sm"
									: "border border-slate-200 bg-white text-slate-700 hover:border-slate-300"
							}`}
						>
							{PREVIEW_VIEWPORT_LABELS[key]}
						</button>
					);
				})}
				{pageId ? <PageConnectionsButton pageId={pageId} pageTitle={pageTitle ?? undefined} /> : null}
				{previewPageUrl ? (
					<a
						href={previewPageUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
					>
						<FiExternalLink className="shrink-0" aria-hidden />
						Preview
					</a>
				) : null}
				{productionPageUrl ? (
					<a
						href={productionPageUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
					>
						<FiExternalLink className="shrink-0" aria-hidden />
						Production
					</a>
				) : null}
			</div>
		</div>
		<div
			className="bg-slate-50 px-2 py-4 flex-1 min-h-[360px] min-w-0 overflow-y-auto overflow-x-hidden rounded-lg border border-slate-200 flex justify-center items-start"
			ref={previewContainerRef}
			style={{
				...(minHeight != null
					? { minHeight: typeof minHeight === "number" ? `${minHeight}px` : minHeight }
					: {}),
				...(contentHeight != null
					? {
							height: typeof contentHeight === "number" ? `${contentHeight}px` : contentHeight,
							minHeight: typeof contentHeight === "number" ? `${contentHeight}px` : contentHeight
					  }
					: {})
			}}
		>
			<style>{previewCss}</style>
			{previewComponents.length === 0 ? (
				<div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500">
					Ajoutez des composants pour afficher un aperçu en direct.
				</div>
			) : (
				<PreviewPane
					components={previewComponents}
					width={previewWidth}
					containerWidth={previewContainerWidth}
					runtimeConfig={runtimeConfig}
					viewport={previewViewport}
					activeComponentIndex={activeComponentIndex}
					onSelectComponent={onSelectComponent}
				/>
			)}
		</div>
		<p className="text-xs text-slate-500">L'aperçu se met à jour instantanément pendant l'édition.</p>
		{previewComponents.length === 0 && (
			<div className="sr-only">
				<LoadingSpinner size="sm" inline />
			</div>
		)}
	</div>
);

export default PageEditorPreviewPanel;
